import { useEffect, useState, type FormEvent } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Package,
  Shield,
} from "lucide-react";

import { useSession } from "@/lib/session";
import { cn } from "@/lib/utils";
import { SiteCredit } from "@/components/SiteCredit";

/**
 * Login do painel.
 *
 * Nao ha cadastro aberto: contas sao criadas pela administracao na tela de
 * Equipe.
 */
export const Route = createFileRoute("/admin/entrar")({
  head: () => ({
    meta: [
      { title: "Entrar no painel — MonteBox" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLogin,
});

function AdminLogin() {
  const navigate = useNavigate();
  const { login, isTeam, profile } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepConnected, setKeepConnected] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Quem ja esta logado nao precisa ver o formulario de novo.
  useEffect(() => {
    if (isTeam) void navigate({ to: "/admin/pedidos", replace: true });
    else if (profile?.role === "REVENDEDOR") void navigate({ to: "/revendedor", replace: true });
  }, [isTeam, profile, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const next = await login(email, password);
      // Revendedor usa esta mesma porta e vai para o painel dele.
      if (next.role === "REVENDEDOR") {
        await navigate({ to: "/revendedor", replace: true });
        return;
      }
      if (next.role !== "ADMIN" && next.role !== "ATENDENTE") {
        // Conta de cliente entrando pela porta da equipe: manda para a area
        // certa em vez de mostrar um painel vazio de permissoes.
        await navigate({ to: "/minha-conta/pedidos", replace: true });
        return;
      }
      await navigate({ to: "/admin/pedidos", replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Nao foi possivel entrar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Painel de marca */}
      <div className="hidden w-[620px] shrink-0 flex-col justify-between bg-mb-navy p-12 lg:flex">
        <Link
          to="/"
          className="flex items-center gap-2.5"
          aria-label="MonteBox — voltar para a loja"
        >
          <span className="grid h-9 w-9 place-items-center rounded-full bg-mb-yellow shadow-cta">
            <Package className="h-5 w-5 text-mb-ink" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-white">MonteBox</span>
          <span className="rounded-full bg-white/12 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.1em] text-mb-yellow">
            Admin
          </span>
        </Link>

        <div>
          <img
            src="/assets/camiseta-base-frente.png"
            alt=""
            className="block h-80 w-80 object-contain"
          />
          <h1 className="mt-8 max-w-[460px] font-display text-4xl/10 font-bold tracking-tight text-white text-pretty">
            O painel de quem cuida dos pedidos.
          </h1>
          <p className="mt-3 max-w-[420px] text-base text-white/72 text-pretty">
            Acompanhe orçamentos, converse pelo WhatsApp e mantenha o catálogo em dia.
          </p>
        </div>

        <p className="text-xs text-white/50">Acesso restrito à equipe MonteBox.</p>
      </div>

      {/* Formulário */}
      <div className="relative flex grow items-center justify-center px-6 pb-16 pt-20 sm:p-12 sm:pb-16 sm:pt-24">
        {/* Volta para a loja — no celular o painel da marca some, e esta é a saída. */}
        <Link
          to="/"
          className="absolute left-6 top-6 inline-flex items-center gap-1.5 text-sm font-semibold text-mb-muted transition-colors hover:text-mb-navy sm:left-12 sm:top-10"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para a loja
        </Link>
        <form className="w-full max-w-[420px]" onSubmit={(event) => void handleSubmit(event)}>
          <h2 className="font-display text-3xl font-bold tracking-tight text-mb-navy">
            Entrar no painel
          </h2>
          <p className="mt-2 text-sm text-mb-muted">Use o e-mail cadastrado pela administração.</p>

          <div className="mt-8 flex flex-col gap-5">
            <label className="block">
              <span className="text-sm font-bold text-mb-ink">E-mail</span>
              <span className="mt-2 flex h-12 items-center gap-2.5 rounded-mb-field border border-mb-line bg-white px-4 focus-within:border-mb-navy focus-within:ring-[3px] focus-within:ring-mb-yellow/35">
                <Mail className="h-[18px] w-[18px] shrink-0 text-mb-muted" />
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="atendimento@exemplo.com.br"
                  className="w-full bg-transparent text-sm text-mb-ink outline-none placeholder:text-mb-muted"
                />
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-bold text-mb-ink">Senha</span>
              <span className="mt-2 flex h-12 items-center gap-2.5 rounded-mb-field border border-mb-line bg-white px-4 focus-within:border-mb-navy focus-within:ring-[3px] focus-within:ring-mb-yellow/35">
                <Lock className="h-[18px] w-[18px] shrink-0 text-mb-muted" />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  className="w-full grow bg-transparent text-base text-mb-ink outline-none placeholder:tracking-[0.2em] placeholder:text-mb-ink"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  className="shrink-0 text-mb-muted transition-colors hover:text-mb-navy"
                >
                  {showPassword ? (
                    <EyeOff className="h-[18px] w-[18px]" />
                  ) : (
                    <Eye className="h-[18px] w-[18px]" />
                  )}
                </button>
              </span>
            </label>

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                role="checkbox"
                aria-checked={keepConnected}
                onClick={() => setKeepConnected((keep) => !keep)}
                className="flex items-center gap-2 text-sm text-mb-muted"
              >
                <span
                  className={cn(
                    "grid h-5 w-5 place-items-center rounded-md border transition-colors",
                    keepConnected ? "border-mb-navy bg-mb-navy" : "border-mb-line bg-white",
                  )}
                >
                  {keepConnected && <Check className="h-3 w-3 stroke-[3] text-white" />}
                </span>
                Manter conectado
              </button>
              <Link
                to="/esqueci-senha"
                search={{ de: "painel" }}
                className="text-sm font-semibold text-mb-navy hover:text-mb-ink"
              >
                Esqueci minha senha
              </Link>
            </div>

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-mb-field border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-mb-button bg-mb-yellow px-6 py-4 font-display text-base font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Entrando…
                </>
              ) : (
                <>
                  Entrar
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>

            <p className="flex items-start gap-2.5 rounded-mb-button border border-mb-line bg-mb-surface px-4 py-3.5 text-xs text-mb-muted">
              <Shield className="mt-px h-[18px] w-[18px] shrink-0 text-mb-navy" />
              Não há cadastro aberto: contas do painel são criadas pela administração na tela de
              Equipe.
            </p>
          </div>
        </form>
        <SiteCredit className="absolute inset-x-6 bottom-4" />
      </div>
    </div>
  );
}
